/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { initializeLocale } from "utilities/UserSettingsUtilities";
import AppRoot from "components/AppRoot";
import ReactDOM from "react-dom";
import "stylesheets/application.scss";
import * as ActiveStorage from "@rails/activestorage";
import "components/shared/WebComponents";
import {
  injectPrivacyScript,
  registerPrivacyEventListeners,
} from "privacy/PrivacyManager";
import { registerUnhandledRejectionListeners } from "utilities/noticeUnhandledRejection";

registerPrivacyEventListeners();
registerUnhandledRejectionListeners();
ActiveStorage.start();

const injectAjoTag = (): Promise<void> => {
  const isTest = JSON.parse(process.env.IS_TEST ?? "false");
  const isSnapshot = navigator.userAgent === "ReactSnap";
  if (!isTest && process.env.AJO_LAUNCH_URL && !isSnapshot) {
    const script = document.createElement("script");
    script.src = "https://www.adobe.com/marketingtech/main.standard.min.js";
    document.body.appendChild(script);
    return new Promise((resolve) => {
      script.onload = (): void => {
        window._satellite.track("pageload", {
          xdm: {},
          data: {},
        });
        resolve();
      };
    });
  } else {
    window._satellite = {
      track: (): void => {},
    };
    return Promise.resolve();
  }
};

Promise.all([injectPrivacyScript(), initializeLocale(), injectAjoTag()])
  .then(() => {
    const root = document.getElementById("root");
    if (root && root.hasChildNodes()) {
      ReactDOM.hydrate(<AppRoot />, root);
    } else {
      ReactDOM.render(<AppRoot />, root);
    }
  })
  .catch((error) => {
    console.error("Error initializing the app:", error);
    newrelic.noticeError(error);
  });
